import { useQuery } from "@tanstack/react-query";
import { buyerSubmissionsApi, type BrokerIntroEmailStateDto } from "@/api/buyers";
import { Button, Skeleton, useToast } from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { InlineQueryError } from "../queryStates";
import { writeClipboard } from "../outreach/outreachCopy";

/** What COPY placed on the clipboard — RECORD SEND sends exactly this back. */
export interface CopiedBrokerEmail {
  subject: string;
  body: string;
}

/**
 * The specialist's own mail client — the second half of the dual send.
 *
 * <p>🔴 <b>Copying RECORDS NOTHING (R4).</b> It used to: fetching the copy was
 * itself the record, on the argument that a separate "mark sent" click is how
 * the funnel gets holes in it. The ruling went the other way — a record must
 * follow a send, not a copy — so this is now a plain READ (a `useQuery`, safe
 * to refetch), and the send is recorded by RECORD SEND on the card, a separate
 * click after the consultant has actually sent it. Until then the card says
 * "Copied — click RECORD SEND once you've sent it."</p>
 *
 * <p>🔴 <b>What goes on the clipboard is the ASSEMBLED message, trailers and
 * all.</b> The buyer intro's copy deliberately omits the signature, because the
 * specialist's own client appends one. This message additionally carries
 * Phoenix's regulated role statement, which no mail client appends — so a copy
 * that showed the editable body alone would be the one path by which this email
 * reaches a listing broker without it.</p>
 *
 * <p>Nothing here writes, so nothing here invalidates; the RECORD SEND
 * mutation on the card refreshes what a record changes.</p>
 */
export function BrokerIntroCopyModal({
  submissionId,
  email,
  onCopied,
  onClose,
}: {
  submissionId: string;
  email: BrokerIntroEmailStateDto;
  /** Called when the MESSAGE reaches the clipboard, with exactly what was
   *  copied — the card then offers RECORD SEND. */
  onCopied: (copied: CopiedBrokerEmail) => void;
  onClose: () => void;
}) {
  const copy = useQuery({
    queryKey: buyerSubmissionKeys.brokerIntroDownload(submissionId),
    queryFn: () => buyerSubmissionsApi.getBrokerIntroDownload(submissionId),
    enabled: Boolean(submissionId),
  });
  const d = copy.data;

  return (
    <Modal
      open
      onClose={onClose}
      title="Send the broker intro yourself"
      size="lg"
      footer={
        <div className="flex justify-end gap-space-3">
          <Button variant="secondary" onClick={onClose} data-testid="broker-intro-copy-done">
            Done
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-4">
        <p className="text-small text-onyx m-0">
          Phoenix will not send anything — you will, from your own email to{" "}
          <strong>{email.agentName ?? "the listing broker"}</strong>
          {email.agentEmail ? (
            <>
              {" "}
              at <strong>{email.agentEmail}</strong>
            </>
          ) : null}
          . Copying records nothing: once you have sent it, click RECORD SEND
          on the card.
        </p>
        {!email.canRecordCopySend ? (
          <p className="text-small text-status-warning m-0" data-testid="broker-intro-copy-blocked">
            {email.copySendBlockedReason}
          </p>
        ) : null}

        {copy.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : copy.isError || !d ? (
          <InlineQueryError
            what="the copy of the broker intro"
            onRetry={() => void copy.refetch()}
            retrying={copy.isFetching}
          />
        ) : (
          <>
            <CopyField label="To" value={d.toEmail} />
            <CopyField label="Subject" value={d.subject} />
            <CopyField
              label="Message"
              value={d.copyText}
              multiline
              testId="broker-intro-copy-text"
              onCopied={() => onCopied({ subject: d.subject, body: d.copyText })}
            />
            <p className="text-caption text-gray-mid m-0">
              Your sign-off and Phoenix&apos;s role statement are already part of
              the text above — paste it whole.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

function CopyField({
  label,
  value,
  multiline,
  testId,
  onCopied,
}: {
  label: string;
  value: string | null;
  multiline?: boolean;
  testId?: string;
  onCopied?: () => void;
}) {
  const toast = useToast();

  if (!value) {
    return (
      <div className="grid gap-space-1">
        <span className="text-caption text-gray-mid uppercase tracking-wider">
          {label}
        </span>
        <p className="text-small text-gray-mid m-0">
          Not on file — use the address you have for this broker.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-space-2">
      <div className="flex items-center justify-between gap-space-2">
        <span className="text-caption text-gray-mid uppercase tracking-wider">
          {label}
        </span>
        <Button
          size="sm"
          variant="text"
          onClick={() =>
            void writeClipboard(value).then((ok) => {
              if (ok) {
                toast.success(`${label} copied.`);
                onCopied?.();
              } else {
                toast.error(`Couldn't copy the ${label.toLowerCase()}. Select it and copy by hand.`);
              }
            })
          }
          data-testid={testId ? `${testId}-button` : undefined}
        >
          Copy {label.toLowerCase()}
        </Button>
      </div>
      <div
        className={
          multiline
            ? "max-h-72 overflow-y-auto rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
            : "rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
        }
      >
        <p
          className="text-small text-onyx whitespace-pre-wrap m-0"
          data-testid={testId}
        >
          {value}
        </p>
      </div>
    </div>
  );
}
